import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';

const STATUSES = [
  ['abierto', 'Abierto'],
  ['en_curso', 'En curso'],
  ['esperando_cliente', 'Esperando cliente'],
  ['resuelto', 'Resuelto'],
  ['cerrado', 'Cerrado'],
];

const emptyForm = {
  customerId: '',
  subject: '',
  description: '',
  priority: 'media',
  category: '',
  assignedTo: '',
  ownerId: '',
  status: 'abierto',
  expectedCompletionDate: '',
  remindAssignee: 'true',
  reminderPeriodDays: '',
  reminderStartDate: '',
  reminderEndDate: '',
};

const TicketForm = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const modal = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    const load = async () => {
      try {
        const [customersRes, usersRes] = await Promise.all([
          api.get('/customers'),
          api.get('/tickets/assignable-users'),
        ]);
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        const users = Array.isArray(usersRes.data) ? usersRes.data : [];
        setAssignableUsers(users);
        const me = users.find(u => u.id === user?.id);
        const defaultUserId = me ? String(me.id) : (users[0] ? String(users[0].id) : '');
        setForm(f => ({ ...f, assignedTo: defaultUserId, ownerId: defaultUserId }));
      } catch (error) {
        modal.error('Error cargando datos para el ticket');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerId) return modal.error('Selecciona un cliente');
    if (!form.subject.trim()) return modal.error('Pon un asunto');
    if (!form.assignedTo) return modal.error('Selecciona el usuario asignado');
    setSaving(true);
    try {
      const payload = {
        customerId: parseInt(form.customerId, 10),
        subject: form.subject.trim(),
        description: form.description,
        priority: form.priority,
        category: form.category,
        assignedTo: parseInt(form.assignedTo, 10),
        ownerId: form.ownerId ? parseInt(form.ownerId, 10) : undefined,
        status: form.status,
        expectedCompletionDate: form.expectedCompletionDate || null,
        remindAssignee: form.remindAssignee === 'true',
        reminderPeriodDays: form.reminderPeriodDays === '' ? null : parseInt(form.reminderPeriodDays, 10),
        reminderStartDate: form.reminderStartDate || null,
        reminderEndDate: form.reminderEndDate || null,
      };
      const res = await api.post('/tickets', payload);
      modal.success(`Ticket ${res.data.code} creado`);
      navigate(`/tickets/${res.data.id}`);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando el ticket');
    } finally {
      setSaving(false);
    }
  };

  if (!hasPermission('tickets', 'create')) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No tienes permiso para crear tickets.</div>;
  }
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>;

  const remindOn = form.remindAssignee === 'true';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Nuevo ticket</h1>
          <p className="page-subtitle">Rellena todos los datos del ticket y, si quieres, programa el aviso al asignado.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 16 }}>Datos del ticket</h2>

          <div className="form-group">
            <label className="form-label">Cliente *</label>
            <select className="form-input" value={form.customerId} onChange={e => setField('customerId', e.target.value)} required>
              <option value="">Seleccionar cliente</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Asunto *</label>
            <input className="form-input" value={form.subject} onChange={e => setField('subject', e.target.value)} required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Asignado a *</label>
              <select className="form-input" value={form.assignedTo} onChange={e => setField('assignedTo', e.target.value)} required>
                <option value="">Seleccionar usuario</option>
                {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Propietario</label>
              <select className="form-input" value={form.ownerId} onChange={e => setField('ownerId', e.target.value)}>
                <option value="">— Mismo que el creador —</option>
                {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Estado</label>
              <select className="form-input" value={form.status} onChange={e => setField('status', e.target.value)}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Prioridad</label>
              <select className="form-input" value={form.priority} onChange={e => setField('priority', e.target.value)}>
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Categoría</label>
              <input className="form-input" value={form.category} onChange={e => setField('category', e.target.value)} placeholder="Ej: Soporte técnico" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha prevista de finalización</label>
              <input type="date" className="form-input" value={form.expectedCompletionDate} onChange={e => setField('expectedCompletionDate', e.target.value)} />
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 4 }}>Para cuándo debería estar acabado. No tiene que ver con el aviso.</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea className="form-input" rows={5} value={form.description} onChange={e => setField('description', e.target.value)} />
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 16 }}>Aviso al asignado</h2>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Recordatorio al asignado</label>
              <select className="form-input" value={form.remindAssignee} onChange={e => setField('remindAssignee', e.target.value)}>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            {remindOn && (
              <div className="form-group">
                <label className="form-label">Cada cuántos días (vacío = un solo aviso)</label>
                <input type="number" min="1" step="1" className="form-input" value={form.reminderPeriodDays} onChange={e => setField('reminderPeriodDays', e.target.value)} placeholder="vacío = un solo aviso" />
              </div>
            )}
          </div>

          {remindOn && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Avisar desde</label>
                <input type="date" className="form-input" value={form.reminderStartDate} onChange={e => setField('reminderStartDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Avisar hasta (vacío = sin fin)</label>
                <input type="date" className="form-input" value={form.reminderEndDate} onChange={e => setField('reminderEndDate', e.target.value)} />
              </div>
            </div>
          )}

          {remindOn && (
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '4px 0 0 0' }}>
              Con la periodicidad vacía, se envía un único aviso en la fecha «desde». Con un número, se repite cada N días hasta «hasta» (o sin fin si lo dejas vacío). La hora del envío es la que esté configurada en Tickets → Avisos, y solo se envía si el avisador automático está activado y el estado del ticket está entre los seleccionados ahí.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/tickets')}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creando...' : 'Crear ticket'}</button>
        </div>
      </form>
    </div>
  );
};

export default TicketForm;
